import type {
  TradeChain,
  TradeChainWithPositions,
  Position,
  PositionWithLegs,
  AlertRule,
  AlertHistory,
  PerformanceSummary,
  StrategyPerformance,
  PnlDataPoint,
  PremiumFlow,
  CreateChainRequest,
  ClosePositionRequest,
  RollPositionRequest,
  SchwabAccount,
  IdeaIssueRow,
  IdeaIssueSnapshot,
  IdeaIssueReason,
  IdeaIssueStrategy,
  JournalEntry,
  PositionAnalysis,
  CandidateMatchKind,
  CandidateLinkState,
  HeldKind,
  HeldFlag,
  HeldSide,
} from '@steward/shared';
// Type-only, so nothing from the component module is pulled into the client bundle graph.
// EventRiskLabel is where both of these shapes are already defined and rendered; re-declaring
// them here would let the client's copy drift from the component that consumes it.
import type { EventRisk, CalendarCoverage } from '@/components/EventRiskLabel';
import type { LegBatchResponse } from '@/lib/odds';

export class ApiError extends Error {
  constructor(
    public status: number,
    message: string
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

let sessionOverlayShown = false;

function showSessionExpiredOverlay() {
  if (sessionOverlayShown) return;
  sessionOverlayShown = true;

  const overlay = document.createElement('div');
  overlay.style.cssText =
    'position:fixed;inset:0;z-index:9999;display:flex;align-items:center;justify-content:center;background:rgba(0,0,0,0.5);backdrop-filter:blur(2px)';

  const card = document.createElement('div');
  card.style.cssText =
    'background:white;border-radius:12px;padding:32px;max-width:360px;text-align:center;box-shadow:0 20px 60px rgba(0,0,0,0.3)';
  card.innerHTML = `
    <svg style="margin:0 auto 16px;display:block" width="48" height="48" fill="none" viewBox="0 0 24 24" stroke="#6b7280">
      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5"
        d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"/>
    </svg>
    <h2 style="margin:0 0 8px;font-size:18px;font-weight:600;color:#111827">Session Expired</h2>
    <p style="margin:0 0 24px;font-size:14px;color:#6b7280;line-height:1.5">
      Your session has expired. Sign in again to continue.
    </p>
    <button id="steward-reauth" style="
      background:#4f46e5;color:white;border:none;padding:10px 24px;border-radius:8px;
      font-size:14px;font-weight:500;cursor:pointer;width:100%
    ">Sign In</button>
  `;

  overlay.appendChild(card);
  document.body.appendChild(overlay);

  document.getElementById('steward-reauth')!.addEventListener('click', () => {
    window.location.reload();
  });
}

const TENANT_STORAGE_KEY = 'steward.tenantId';

export function getActiveTenantId(): string | null {
  try {
    return localStorage.getItem(TENANT_STORAGE_KEY);
  } catch {
    return null;
  }
}

export function setActiveTenantId(tenantId: string | null): void {
  try {
    if (tenantId) localStorage.setItem(TENANT_STORAGE_KEY, tenantId);
    else localStorage.removeItem(TENANT_STORAGE_KEY);
  } catch {
    // localStorage unavailable — server falls back to the default tenant
  }
}

// Matches exactly what Date.toISOString() emits — and nothing else. Applied
// only to keys ending in `At`/`Date` (case-sensitive), so user text (note,
// body, message, title) and day-key strings (bare `date`, YYYY-MM-DD) are
// never touched. See CLAUDE.md "API / Web Client Dates" for the naming rule
// this depends on.
const ISO_DATE_RE = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}\.\d{3}Z$/;

export function apiDateReviver(key: string, value: unknown): unknown {
  if (
    typeof value === 'string' &&
    (key.endsWith('At') || key.endsWith('Date')) &&
    ISO_DATE_RE.test(value)
  ) {
    const d = new Date(value);
    if (!Number.isNaN(d.getTime())) return d;
  }
  return value;
}

async function fetchApi<T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<T> {
  const url = `/api${endpoint}`;
  const tenantId = getActiveTenantId();
  const response = await fetch(url, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...(tenantId ? { 'X-Tenant-Id': tenantId } : {}),
      ...options.headers,
    },
    credentials: 'include',
  });

  // Detect Cloudflare Access session expiry:
  // - fetch follows 302 redirects, so response.redirected is true
  // - or Access returns 401/403 directly
  // In either case, the response won't be JSON — show re-auth overlay
  if (
    response.redirected ||
    response.status === 401 ||
    response.status === 403
  ) {
    const contentType = response.headers.get('content-type') || '';
    if (!contentType.includes('application/json')) {
      showSessionExpiredOverlay();
      return new Promise(() => {});
    }
  }

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Unknown error' }));
    throw new ApiError(response.status, error.error || error.message || 'Request failed');
  }

  return JSON.parse(await response.text(), apiDateReviver);
}

// Auth
export type TenantSummary = {
  id: string;
  email: string;
  name: string | null;
  role: 'owner' | 'viewer';
};

export const authApi = {
  me: () =>
    fetchApi<{
      user: { id: string | null; email: string; name: string | null };
      tenants: TenantSummary[];
      activeTenantId: string | null;
      activeTenantRole: 'owner' | 'viewer';
    }>('/auth/me'),
};

// Tenant sharing
export const tenantApi = {
  getMembers: () =>
    fetchApi<{ members: Array<{ id: string; email: string; role: 'owner' | 'viewer'; addedAt: Date }> }>(
      '/tenant/members'
    ),
  addMember: (email: string, role: 'owner' | 'viewer' = 'viewer') =>
    fetchApi<{ member: { id: string; email: string; role: 'owner' | 'viewer'; addedAt: Date } }>(
      '/tenant/members',
      { method: 'POST', body: JSON.stringify({ email, role }) }
    ),
  removeMember: (id: string) =>
    fetchApi<{ success: boolean }>(`/tenant/members/${id}`, { method: 'DELETE' }),
};

// Trade Chains
export const chainsApi = {
  list: (params?: { status?: string; symbol?: string; limit?: number; offset?: number }) => {
    const searchParams = new URLSearchParams();
    if (params?.status) searchParams.set('status', params.status);
    if (params?.symbol) searchParams.set('symbol', params.symbol);
    if (params?.limit) searchParams.set('limit', params.limit.toString());
    if (params?.offset) searchParams.set('offset', params.offset.toString());
    const query = searchParams.toString();
    return fetchApi<{ chains: TradeChainWithPositions[] }>(`/chains${query ? `?${query}` : ''}`);
  },

  get: (id: string) =>
    fetchApi<{ chain: TradeChainWithPositions }>(`/chains/${id}`),

  create: (data: CreateChainRequest) =>
    fetchApi<{ chain: TradeChainWithPositions }>('/chains', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  update: (id: string, data: { notes?: string; tags?: string[] }) =>
    fetchApi<{ chain: TradeChain }>(`/chains/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    }),

  delete: (id: string) =>
    fetchApi<{ success: boolean }>(`/chains/${id}`, {
      method: 'DELETE',
    }),

  getHistory: (id: string) =>
    fetchApi<{
      chain: TradeChainWithPositions;
      timeline: Array<{
        type: 'opened' | 'rolled' | 'closed';
        date: string;
        position: PositionWithLegs;
      }>;
    }>(`/chains/${id}/history`),

  getOrphans: () =>
    fetchApi<{
      orphans: Array<{
        chain: {
          id: string;
          underlyingSymbol: string;
          strategyType: string;
          status: string;
          openedAt: Date;
          closedAt: Date | null;
        };
        position: PositionWithLegs;
        suggestions: Array<{
          chainId: string;
          underlyingSymbol: string;
          strategyType: string;
          positionCount: number;
          status: string;
          daysDifference: number;
          isLikelyRoll: boolean;
        }>;
      }>;
      totalOrphans: number;
    }>('/chains/orphans'),

  unlinkPosition: (chainId: string, positionId: string) =>
    fetchApi<{ success: boolean; newChain: TradeChainWithPositions }>(
      `/chains/${chainId}/positions/${positionId}/unlink`,
      { method: 'POST' }
    ),

  linkPosition: (targetChainId: string, positionId: string) =>
    fetchApi<{ success: boolean; chain: TradeChainWithPositions }>(
      `/chains/${targetChainId}/link-position`,
      {
        method: 'POST',
        body: JSON.stringify({ positionId }),
      }
    ),

  openPnl: () =>
    fetchApi<{
      pnl: Record<string, { estimatedPnl: number; costToClose: number }>;
    }>('/chains/open-pnl'),

  listAttachmentDecisions: () =>
    fetchApi<{
      decisions: Array<{
        id: string;
        underlyingSymbol: string;
        reason: 'multi_chain' | 'qty_over' | 'same_side_different_strikes';
        newChain: unknown;
        candidateChains: unknown[];
        createdAt: Date;
      }>;
    }>('/chains/attachment-decisions'),

  resolveAttachmentDecision: (id: string, body: { action: 'merge' | 'keep_separate'; targetChainId?: string }) =>
    fetchApi<{ success: boolean }>(`/chains/attachment-decisions/${id}`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
};

// Positions
export const positionsApi = {
  get: (id: string) =>
    fetchApi<{ position: PositionWithLegs }>(`/positions/${id}`),

  close: (id: string, data: ClosePositionRequest) =>
    fetchApi<{ position: Position; chain: TradeChain }>(`/positions/${id}/close`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  roll: (id: string, data: RollPositionRequest) =>
    fetchApi<{ oldPosition: Position; newPosition: PositionWithLegs; chain: TradeChain }>(
      `/positions/${id}/roll`,
      {
        method: 'POST',
        body: JSON.stringify(data),
      }
    ),
};

// Alerts
export const alertsApi = {
  listRules: () => fetchApi<{ rules: AlertRule[] }>('/alerts/rules'),

  createRule: (data: Omit<AlertRule, 'id' | 'userId' | 'createdAt' | 'updatedAt' | 'lastTriggeredAt'>) =>
    fetchApi<{ rule: AlertRule }>('/alerts/rules', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  updateRule: (id: string, data: Partial<AlertRule>) =>
    fetchApi<{ rule: AlertRule }>(`/alerts/rules/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    }),

  deleteRule: (id: string) =>
    fetchApi<{ success: boolean }>(`/alerts/rules/${id}`, {
      method: 'DELETE',
    }),

  listHistory: (params?: { unreadOnly?: boolean; limit?: number }) => {
    const searchParams = new URLSearchParams();
    if (params?.unreadOnly) searchParams.set('unread', 'true');
    if (params?.limit) searchParams.set('limit', params.limit.toString());
    const query = searchParams.toString();
    return fetchApi<{ alerts: AlertHistory[] }>(`/alerts/history${query ? `?${query}` : ''}`);
  },

  markRead: (id: string) =>
    fetchApi<{ success: boolean }>(`/alerts/history/${id}/read`, {
      method: 'POST',
    }),

  markAllRead: () =>
    fetchApi<{ success: boolean }>('/alerts/history/read-all', {
      method: 'POST',
    }),

  unreadCount: () =>
    fetchApi<{ count: number }>('/alerts/unread-count'),
};

// Push Notifications
export const pushApi = {
  getVapidKey: () =>
    fetchApi<{ publicKey: string }>('/push/vapid-key'),

  subscribe: (subscription: { endpoint: string; keys: { p256dh: string; auth: string } }) =>
    fetchApi<{ success: boolean; subscriptionId: string }>('/push/subscribe', {
      method: 'POST',
      body: JSON.stringify(subscription),
    }),

  unsubscribe: (endpoint: string) =>
    fetchApi<{ success: boolean }>('/push/unsubscribe', {
      method: 'POST',
      body: JSON.stringify({ endpoint }),
    }),

  test: () =>
    fetchApi<{ results: Array<{ success: boolean; error?: string }> }>('/push/test', {
      method: 'POST',
    }),
};

// Analytics
/**
 * `GET /analytics/capital`. `expiration` / `freesUpOn` are `YYYY-MM-DD` day labels and stay
 * strings; `readAt` is a genuine instant and comes back as a real `Date` through
 * `apiDateReviver` (the `At` suffix is what the reviver keys on).
 */
export interface CapitalResponse {
  held: {
    total: number;
    byExpiration: Array<{ expiration: string; held: number; chains: number }>;
    byChain: Array<{
      chainId: string;
      symbol: string;
      held: number | null;
      kinds: HeldKind[];
      flags: HeldFlag[];
      freesUpOn: string | null;
      sides: HeldSide[];
    }>;
  };
  // null when Schwab is unreachable or not connected -- the held half is always present.
  account: {
    kind: 'CASH' | 'MARGIN' | null;
    cashAvailableForTrading: number | null;
    optionBuyingPower: number | null;
    readAt: Date;
    accountsRead: number;
  } | null;
}

export const analyticsApi = {
  getCapital: () => fetchApi<CapitalResponse>('/analytics/capital'),

  getSummary: (params?: { start?: string; end?: string }) => {
    const searchParams = new URLSearchParams();
    if (params?.start) searchParams.set('start', params.start);
    if (params?.end) searchParams.set('end', params.end);
    const query = searchParams.toString();
    return fetchApi<{ summary: PerformanceSummary }>(`/analytics/summary${query ? `?${query}` : ''}`);
  },

  getByStrategy: () =>
    fetchApi<{ strategies: StrategyPerformance[] }>('/analytics/by-strategy'),

  getPnlHistory: (period?: string) => {
    const query = period ? `?period=${period}` : '';
    return fetchApi<{ dataPoints: PnlDataPoint[] }>(`/analytics/pnl-history${query}`);
  },

  getPremiumFlow: (params?: { period?: string; symbol?: string }) => {
    const sp = new URLSearchParams();
    if (params?.period) sp.set('period', params.period);
    if (params?.symbol) sp.set('symbol', params.symbol);
    const query = sp.toString() ? `?${sp.toString()}` : '';
    return fetchApi<{ flow: PremiumFlow }>(`/analytics/premium-flow${query}`);
  },

  getSymbols: () =>
    fetchApi<{
      symbols: Array<{
        symbol: string;
        tradesCount: number;
        openCount: number;
        closedCount: number;
        totalPnl: number;
        premiumCollected: number;
        premiumPaid: number;
      }>;
    }>('/analytics/symbols'),

  getSymbolInsights: () =>
    fetchApi<{
      symbols: Array<{
        symbol: string;
        tradesCount: number;
        openCount: number;
        closedCount: number;
        totalPnl: number;
        premiumCollected: number;
        premiumPaid: number;
        netPremium: number;
        winCount: number;
        lossCount: number;
        winRate: number | null;
        firstTradeAt: Date;
        lastTradeAt: Date;
        lastClosedAt: Date | null;
        daysSinceLastTrade: number;
        strategies: string[];
        chains: Array<{
          id: string;
          strategyType: string;
          status: string;
          openedAt: Date;
          closedAt: Date | null;
          realizedPnl: number;
          netPremium: number;
        }>;
        pnlProgression: Array<{ date: string; pnl: number; cumPnl: number }>;
      }>;
      idleStocks: string[];
    }>('/analytics/symbol-insights'),
};

/**
 * `GET /goals/credit`. Every field here is a number, a boolean or a `YYYY-MM` month LABEL — there
 * is no instant in this payload, so nothing is revived and nothing should be: a field named
 * `month` deliberately misses the `At`/`Date` suffix `apiDateReviver` keys on.
 */
export interface CreditMonth {
  month: string;
  gross: number;
  costs: number;
  net: number;
  goal: number | null;
  remaining: number | null;
  over: number | null;
  hit: boolean | null;
  /** `remaining`/`over`/`hit`/`paceNeeded` are on NET; these are the same gap on gross. */
  grossRemaining: number | null;
  grossOver: number | null;
  grossHit: boolean | null;
  isCurrent: boolean;
  daysLeft: number | null;
  paceNeeded: number | null;
}

/** One event's share of a month. `occurredAt` is revived; `day` and `newExpiration` are labels. */
export interface CreditLine {
  fingerprint: string;
  chainId: string;
  symbol: string;
  eventType: 'opened' | 'rolled' | 'closed' | 'expired' | 'assigned' | 'exercised';
  occurredAt: Date;
  /** 'YYYY-MM-DD' in the user's timezone. */
  day: string;
  strategyLabel: string | null;
  side: 'put' | 'call' | 'both' | null;
  /** Rolls only: 'YYYY-MM-DD'. */
  newExpiration: string | null;
  gross: number;
  costs: number;
}

/** The broker's own view of the month from its cash ledger; null when it has no rows for it. */
export interface BrokerMonth {
  opening: number;
  closing: number;
  fees: number;
  rows: number;
  noEffect: number;
}

export interface CreditMonthLines {
  month: string;
  lines: CreditLine[];
  gross: number;
  costs: number;
  net: number;
  /** Signed cash, the broker's framing: what `broker.opening` / `broker.closing` compare to. */
  openingCash: number;
  closingCash: number;
  broker: BrokerMonth | null;
  /** Events that moved no cash (expired, assigned, exercised) and are not listed. */
  unlisted: number;
}

export interface CreditGoalResponse {
  goal: number | null;
  months: CreditMonth[];
}

export const goalsApi = {
  getCredit: (months?: number) =>
    fetchApi<CreditGoalResponse>(`/goals/credit${months ? `?months=${months}` : ''}`),

  getMonthLines: (month: string) =>
    fetchApi<CreditMonthLines>(`/goals/credit/${month}/events`),

  setGoal: (goal: number | null) =>
    fetchApi<{ goal: number | null }>('/goals/credit', {
      method: 'PUT',
      body: JSON.stringify({ goal }),
    }),

  setOverride: (month: string, target: number | null) =>
    fetchApi<{ month: string; target: number | null }>(`/goals/credit/${month}`, {
      method: 'PUT',
      body: JSON.stringify({ target }),
    }),
};

// Quotes
interface SpreadSuggestion {
  expiration: string;
  dte: number;
  shortStrike: number;
  longStrike: number;
  shortDelta: number;
  longDelta: number | null;
  credit: number;
  spreadWidth: number;
  shortTheta: number | null;
  shortIV: number | null;
  shortBid?: number | null;
  shortAsk?: number | null;
  shortMark?: number | null;
  longMark?: number | null;
  // Annotated per side by the API. Event proximity is a property of the
  // expiration, so every side on one row carries identical values.
  // Date, not string -- apiDateReviver revives *Date fields on the way in.
  earningsInWindow?: boolean;
  earningsDate?: Date | null;
  earningsTiming?: 'bmo' | 'amc' | null;
  exDivInWindow?: boolean;
  exDivDate?: Date | null;
  exDivAmount?: number | null;
  exDivExceedsExtrinsic?: boolean;
}

export const quotesApi = {
  get: (symbols: string[]) =>
    fetchApi<{
      quotes: Array<{
        symbol: string;
        price: number;
        change: number;
        changePercent: number;
        bid?: number;
        ask?: number;
      }>;
    }>(`/quotes?symbols=${symbols.join(',')}`),

  getOption: (symbol: string) =>
    fetchApi<{
      quote: {
        symbol: string;
        bid: number;
        ask: number;
        last: number;
        delta?: number;
        theta?: number;
        impliedVolatility?: number;
      };
    }>(`/quotes/option/${encodeURIComponent(symbol)}`),

  getOptionLegs: (
    underlyingSymbol: string,
    legs: Array<{
      optionType: 'call' | 'put';
      strikePrice: number;
      expirationDate: string;
    }>
  ) =>
    fetchApi<{
      underlying: {
        symbol: string;
        price: number;
        change: number;
        changePercent: number;
      } | null;
      legs: Array<{
        optionType: 'call' | 'put';
        strikePrice: number;
        expirationDate: string;
        bid: number | null;
        ask: number | null;
        mark: number | null;
        last: number | null;
        delta: number | null;
        theta: number | null;
        impliedVolatility: number | null;
      }>;
    }>('/quotes/options/legs', {
      method: 'POST',
      body: JSON.stringify({ underlyingSymbol, legs }),
    }),

  /**
   * Every Risk-board row's legs in one round trip. `expirationDate` is a YYYY-MM-DD day label,
   * not an instant — it does not match the reviver's `toISOString()` shape, so it stays a string
   * on the way out and on the way back, which is exactly what `LegRequest` wants.
   */
  getOptionLegsBatch: (
    groups: Array<{
      id: string;
      underlyingSymbol: string;
      legs: Array<{ optionType: 'call' | 'put'; strikePrice: number; expirationDate: string }>;
    }>
  ) =>
    fetchApi<LegBatchResponse>('/quotes/options/legs/batch', {
      method: 'POST',
      body: JSON.stringify({ groups }),
    }),

  getRollScenarios: (
    underlyingSymbol: string,
    legs: Array<{
      optionType: 'call' | 'put';
      strikePrice: number;
      expirationDate: string;
      action: 'BTO' | 'STO' | 'BTC' | 'STC';
      quantity: number;
    }>,
    targetStrikes?: Array<{
      optionType: 'call' | 'put';
      originalStrikePrice: number;
      strikePrice: number;
    }>
  ) =>
    fetchApi<{
      costToClose: number | null;
      currentExpiration: string;
      scenarios: Array<{
        expiration: string;
        dte: number;
        legs: Array<{
          optionType: string;
          strikePrice: number;
          action: string;
          mark: number | null;
          bid: number | null;
          ask: number | null;
          delta: number | null;
          theta: number | null;
          openInterest: number | null;
        }>;
        rollCredit: number | null;
        netRollPnl: number | null;
        // Annotated by the API's annotateWithCalendar. `earningsDate` and
        // `exDivDate` MUST be Date, not string: fetchApi runs apiDateReviver,
        // which revives any field ending in `At` or `Date`. Typing them string
        // compiles and then throws on the first .getUTCDate().
        earningsInWindow: boolean;
        earningsDate: Date | null;
        earningsTiming: 'bmo' | 'amc' | null;
        exDivInWindow: boolean;
        exDivDate: Date | null;
        exDivAmount: number | null;
        exDivExceedsExtrinsic: boolean;
      }>;
      calendarCoverage?: { hasData: boolean; lastConfirmedAt: Date | null };
    }>('/quotes/options/roll-scenarios', {
      method: 'POST',
      body: JSON.stringify({ underlyingSymbol, legs, targetStrikes }),
    }),

  getSpreadSuggestions: (params: {
    underlyingSymbol: string;
    existingSide: 'put' | 'call';
    spreadWidth: number;
    quantity: number;
    targetDeltaMin?: number;
    targetDeltaMax?: number;
  }) =>
    fetchApi<{
      suggestSide: 'put' | 'call';
      underlyingPrice: number;
      deltaRange: { min: number; max: number };
      suggestions: Array<{
        expiration: string;
        dte: number;
        shortStrike: number;
        longStrike: number;
        shortDelta: number;
        longDelta: number | null;
        credit: number;
        spreadWidth: number;
        shortTheta: number | null;
        shortIV: number | null;
        // Date, not string -- apiDateReviver revives *Date fields on the way in.
        earningsInWindow: boolean;
        earningsDate: Date | null;
        earningsTiming: 'bmo' | 'amc' | null;
        exDivInWindow: boolean;
        exDivDate: Date | null;
        exDivAmount: number | null;
        exDivExceedsExtrinsic: boolean;
      }>;
      calendarCoverage?: { hasData: boolean; lastConfirmedAt: Date | null };
    }>('/quotes/options/spread-suggestions', {
      method: 'POST',
      body: JSON.stringify(params),
    }),

  getTradeSuggestions: (params: {
    underlyingSymbol: string;
    spreadWidth?: number;
    quantity?: number;
    targetDeltaMin?: number;
    targetDeltaMax?: number;
    sharesHeld?: number;
  }) =>
    fetchApi<{
      symbol: string;
      underlyingPrice: number;
      deltaRange: { min: number; max: number };
      spreadWidth: number;
      quantity: number;
      source?: 'schwab' | 'tradier' | 'yahoo';
      suggestions: Array<{
        expiration: string;
        dte: number;
        putSpread: SpreadSuggestion | null;
        callSpread: SpreadSuggestion | null;
        ironCondor: { putSide: SpreadSuggestion; callSide: SpreadSuggestion; totalCredit: number } | null;
        // Annotated server-side by buildCoveredCallSuggestion, same as the
        // spreads. Declaring it without these dropped the annotation at the type
        // boundary on the one row shape that can carry no spread at all.
        // `earningsDate`/`exDivDate` are Date, not string — apiDateReviver
        // revives *Date fields on the way in.
        coveredCall: {
          strike: number; delta: number; premium: number; contracts: number;
          mark: number; theta: number | null; iv: number | null;
          earningsInWindow?: boolean;
          earningsDate?: Date | null;
          earningsTiming?: 'bmo' | 'amc' | null;
          exDivInWindow?: boolean;
          exDivDate?: Date | null;
          exDivAmount?: number | null;
          exDivExceedsExtrinsic?: boolean;
        } | null;
      }>;
      calendarCoverage?: { hasData: boolean; lastConfirmedAt: Date | null };
    }>('/quotes/options/trade-suggestions', {
      method: 'POST',
      body: JSON.stringify(params),
    }),

  getMarketStatus: () =>
    fetchApi<{
      isOpen: boolean;
      reason: string;
      date: string;
      openTime: string;
      closeTime: string;
      isEarlyClose: boolean;
      isTradingDay: boolean;
    }>('/quotes/market-status'),
};

// Ideas
export const ideasApi = {
  reportIssue: (payload: {
    symbol: string;
    strategy: IdeaIssueStrategy;
    expiration: string;
    reasons: IdeaIssueReason[];
    note?: string;
    snapshot: IdeaIssueSnapshot;
  }) =>
    fetchApi<{ issue: IdeaIssueRow }>('/ideas/issues', {
      method: 'POST',
      body: JSON.stringify(payload),
    }),
  listIssues: () => fetchApi<{ issues: IdeaIssueRow[] }>('/ideas/issues'),
};

// Candidates -- the options bot's iron condor shortlist, re-priced live at read time.
//
// Date discipline on this shape is not uniform, and the split is the API's, not a slip:
// `expiration` and `earningsDate` are YYYY-MM-DD DAY LABELS and stay `string` (apiDateReviver
// only revives the exact Date.toISOString() shape). Everything named `*At`, plus the calendar's
// `earningsDate`/`exDivDate` (UTC-midnight instants), comes back as a real `Date`.
export interface CandidateRunSummary {
  id: string;
  scan: string;
  reportDate: string;
  snapshotAt: Date;
  rowsInScan: number;
  rowsEvaluated: number;
  postedBy: string;
  createdAt: Date;
  counts: { candidates: number; disqualified: number; notEvaluated: number; settled: number };
  insideRate: number | null;
}

export interface CandidateLive {
  source: 'schwab' | 'tradier' | 'yahoo';
  underlyingPrice: number | null;
  /** Short marks minus long marks -- the credit at the midpoint. */
  mid: number | null;
  /** Short bids minus long asks -- the credit if you cross every spread. */
  natural: number | null;
  shortPutDelta: number | null;
  shortCallDelta: number | null;
  /** The short call's live mark. Carried for the ex-div assessment; not rendered. */
  shortCallMark: number | null;
  expectedMove: number | null;
  roomPutPct: number | null;
  roomCallPct: number | null;
  quotedAt: Date;
}

export interface CandidateOutcome {
  status: 'pending' | 'settled' | 'unavailable';
  settledAt: Date | null;
  settlementPrice: number | null;
  finished: 'inside' | 'below_put' | 'above_call' | null;
  theoreticalPnl: number | null;
  touchedPut: boolean;
  touchedCall: boolean;
  worstClose: number | null;
  daysTracked: number;
}

export interface CandidateDetail {
  id: string;
  rank: number | null;
  ticker: string;
  expiration: string;
  dte: number | null;
  shortPut: number;
  shortPutDelta: number;
  shortCall: number;
  shortCallDelta: number;
  longPut: number;
  longCall: number;
  width: number;
  credit: number;
  maxLoss: number;
  creditPctOfWidth: number;
  probabilityOfProfit: number | null;
  ivRank: number | null;
  scoreFundamental: number | null;
  scoreGrowth: number | null;
  technical: number | null;
  putBidAsk: number | null;
  putOpenInterest: number | null;
  callBidAsk: number | null;
  callOpenInterest: number | null;
  chart: string | null;
  chartTouch: boolean | null;
  events: string[];
  flags: string[];
  notes: string | null;
  earningsDate: string | null;
  earningsSource: string | null;
  earningsVerified: boolean | null;
  strategyReadAt: Date | null;
  live: CandidateLive | null;
  calendar: EventRisk | null;
  /**
   * Whether the nightly sweep has ever recorded anything for this ticker. Separate from
   * `calendar` on purpose: an unswept symbol and a swept one with no events both assess to
   * all-false, and the table must not render the first as "checked, nothing found".
   */
  calendarCoverage: CalendarCoverage;
  outcome: CandidateOutcome | null;
  /** The bot's underlying price at scan time, as reported. Distinct from `live.underlyingPrice`. */
  price: number | null;
  /**
   * The chain this candidate was actually traded as, when the matcher linked it. Null with
   * `linkState: 'linked'` is not a contradiction — a replay regenerates every chain id, so the
   * link survives pointing at a chain that no longer exists until the matcher repairs it. The
   * table renders that as "link lost after replay", never as "not traded".
   */
  chainId: string | null;
  matchKind: CandidateMatchKind | null;
  matchedAt: Date | null;
  linkState: CandidateLinkState | null;
}

export interface CandidateOtherRow {
  id: string;
  kind: 'disqualified' | 'not_evaluated';
  ticker: string;
  expiration: string;
  stage: string | null;
  reason: string | null;
}

export interface CandidateScoreRow {
  settled: number;
  inside: number;
  insideRate: number | null;
  meanPnl: number | null;
  medianPnl: number | null;
}

export type CandidateTechnicalBand = '1-3' | '4-6' | '7-8' | '9-10' | 'none';

export const candidatesApi = {
  // fetchApi throws ApiError carrying the server's `error` text on non-2xx, which is how the
  // upload control surfaces a rejected report rather than a bare "failed".
  uploadRun: (report: unknown) =>
    fetchApi<{
      runId: string;
      candidates: number;
      disqualified: number;
      notEvaluated: number;
      replaced: boolean;
      /** Candidates the intake matched to an already-open chain of yours. */
      linked: number;
    }>('/candidates/runs', { method: 'POST', body: JSON.stringify(report) }),
  listRuns: (limit = 20) => fetchApi<{ runs: CandidateRunSummary[] }>(`/candidates/runs?limit=${limit}`),
  getRun: (id: string) =>
    fetchApi<{
      run: CandidateRunSummary;
      candidates: CandidateDetail[];
      disqualified: CandidateOtherRow[];
      notEvaluated: CandidateOtherRow[];
    }>(`/candidates/runs/${id}`),
  scoreboard: () =>
    fetchApi<{
      allTime: CandidateScoreRow;
      byRun: Array<{ runId: string; reportDate: string } & CandidateScoreRow>;
      byFlag: Record<string, CandidateScoreRow>;
      byTechnical: Record<CandidateTechnicalBand, CandidateScoreRow>;
      /**
       * The bot's picks split by whether Todd actually traded them. `realizedMeanPnl` is his
       * own per-share result on the traded side; every other figure on both sides is the
       * theoretical hold-to-expiration number, so the two columns stay comparable.
       */
      byTraded: {
        traded: CandidateScoreRow & { realizedMeanPnl: number | null };
        notTraded: CandidateScoreRow;
      };
    }>('/candidates/scoreboard'),
  /** Links every unlinked candidate it can, and repairs links a replay broke. Idempotent. */
  linkAll: () =>
    fetchApi<{ linked: number; skipped: number; errors: number; relinked: number; lost: number }>(
      '/candidates/link',
      { method: 'POST' }
    ),
  unlink: (id: string) => fetchApi<{ ok: true }>(`/candidates/${id}/link`, { method: 'DELETE' }),
  link: (id: string, chainId: string) =>
    fetchApi<{ ok: true; kind: CandidateMatchKind }>(`/candidates/${id}/link`, {
      method: 'POST',
      body: JSON.stringify({ chainId }),
    }),
};

// Journal
export const journalApi = {
  list: (params: { symbol?: string; chainId?: string; before?: number; beforeId?: string; limit?: number }) => {
    const q = new URLSearchParams();
    if (params.symbol) q.set('symbol', params.symbol);
    if (params.chainId) q.set('chainId', params.chainId);
    if (params.before) q.set('before', String(params.before));
    if (params.beforeId) q.set('beforeId', params.beforeId);
    if (params.limit) q.set('limit', String(params.limit));
    return fetchApi<{ entries: JournalEntry[]; nextCursor: { before: number; beforeId: string } | null }>(
      `/journal?${q.toString()}`
    );
  },
  addNote: (id: string, note: string | null) =>
    fetchApi<{ entry: JournalEntry }>(`/journal/${id}/note`, {
      method: 'PATCH',
      body: JSON.stringify({ note }),
    }),
  createEntry: (payload: { body: string; symbol: string; chainId?: string }) =>
    fetchApi<{ entry: JournalEntry }>('/journal', {
      method: 'POST',
      body: JSON.stringify(payload),
    }),
  /**
   * One bounded batch of journal history backfill (Finding I4) — the server processes at most
   * `CAPTURE_BATCH_CHAINS` chains per call and reports whether more remain. Pass the previous
   * call's `cursor` to resume; omit/undefined to start over. See `runJournalBackfillUntilDone`
   * below for the loop-until-done driver both callers (`ToolsPage.tsx`, `JsonImport.tsx`) use.
   */
  backfill: (cursor?: string | null) =>
    fetchApi<JournalBackfillPage>(
      `/journal/backfill${cursor ? `?cursor=${encodeURIComponent(cursor)}` : ''}`,
      { method: 'POST' },
    ),
};

export interface JournalBackfillPage {
  eventsInserted: number;
  entriesInserted: number;
  /** Stored events whose re-derived payload differed and were rewritten (a roll whose close
   *  landed in a later sync), and the automatic journal lines rewritten with them. */
  eventsUpdated: number;
  entriesUpdated: number;
  /** Stored events a re-derived chain no longer produces, and their automatic lines, removed. */
  eventsRemoved: number;
  entriesRemoved: number;
  /** Chains processed in THIS batch — not a running total. */
  chainsProcessed: number;
  totalChains: number;
  done: boolean;
  cursor: string | null;
}

export interface JournalBackfillTotals {
  eventsInserted: number;
  entriesInserted: number;
  eventsUpdated: number;
  entriesUpdated: number;
  eventsRemoved: number;
  entriesRemoved: number;
  chainsProcessed: number;
  totalChains: number;
}

/**
 * Drives `journalApi.backfill` to completion, one bounded batch per call, accumulating totals
 * across pages. Pure aside from the injected `backfill` function — testable without a fetch
 * mock, same convention as `runReplayWithFollowUps` (`ToolsPage.tsx`).
 */
export async function runJournalBackfillUntilDone(
  backfill: (cursor?: string | null) => Promise<JournalBackfillPage>,
  onProgress?: (state: { chainsProcessed: number; totalChains: number }) => void,
): Promise<JournalBackfillTotals> {
  let cursor: string | null | undefined;
  let eventsInserted = 0;
  let entriesInserted = 0;
  let eventsUpdated = 0;
  let entriesUpdated = 0;
  let eventsRemoved = 0;
  let entriesRemoved = 0;
  let chainsProcessed = 0;
  let totalChains = 0;

  for (;;) {
    const page = await backfill(cursor);
    eventsInserted += page.eventsInserted;
    entriesInserted += page.entriesInserted;
    eventsUpdated += page.eventsUpdated;
    entriesUpdated += page.entriesUpdated;
    // A page from an API deployed before these counters existed carries none: read as 0.
    eventsRemoved += page.eventsRemoved ?? 0;
    entriesRemoved += page.entriesRemoved ?? 0;
    chainsProcessed += page.chainsProcessed;
    totalChains = page.totalChains;
    onProgress?.({ chainsProcessed, totalChains });
    if (page.done) break;
    cursor = page.cursor;
  }

  return {
    eventsInserted, entriesInserted, eventsUpdated, entriesUpdated, eventsRemoved, entriesRemoved,
    chainsProcessed, totalChains,
  };
}

// Schwab
export const schwabApi = {
  getConfigStatus: () =>
    fetchApi<{
      configured: boolean;
      details: { clientId: boolean; clientSecret: boolean; redirectUri: boolean };
    }>('/schwab/config/status'),

  getAuthUrl: () => fetchApi<{ url: string }>('/schwab/auth/url'),

  callback: (code: string) =>
    fetchApi<{
      success: boolean;
      accountsLinked: number;
      syncResults?: Array<{
        accountId: string;
        accountNumber: string;
        success: boolean;
        ordersProcessed?: number;
        error?: string;
      }>;
    }>('/schwab/auth/callback', {
      method: 'POST',
      body: JSON.stringify({ code }),
    }),

  getAccounts: () => fetchApi<{ accounts: SchwabAccount[] }>('/schwab/accounts'),

  refreshAccounts: () =>
    fetchApi<{ success: boolean; accountsLinked: number; accounts: SchwabAccount[] }>(
      '/schwab/accounts/refresh',
      { method: 'POST' }
    ),

  sync: (accountId?: string, syncType: 'full' | 'incremental' = 'full') =>
    fetchApi<{
      results: Array<{
        accountId: string;
        accountNumber: string;
        success: boolean;
        ordersProcessed?: number;
        ordersFailed?: number;
        chainsCreated?: number;
        positionsCreated?: number;
        error?: string;
      }>;
    }>('/schwab/sync', {
      method: 'POST',
      body: JSON.stringify({ accountId, syncType }),
    }),

  syncHistory: (limit = 20) =>
    fetchApi<{
      logs: Array<{
        id: string;
        syncType: 'full' | 'incremental';
        status: 'pending' | 'running' | 'completed' | 'failed';
        ordersProcessed: number;
        ordersFailed: number;
        errorMessage: string | null;
        startedAt: Date;
        completedAt: Date | null;
      }>;
    }>(`/schwab/sync/history?limit=${limit}`),

  disconnect: () =>
    fetchApi<{ success: boolean }>('/schwab/disconnect', {
      method: 'DELETE',
    }),

  getStatus: () =>
    fetchApi<{
      connected: boolean;
      reason?: 'no_tokens' | 'revoked' | 'expired' | 'refresh_failed';
      email?: string;
      accessTokenExpiresAt?: Date;
      refreshTokenExpiresAt?: Date;
      lastRefreshAt?: Date;
      accounts: Array<{
        id: string;
        accountNumber: string;
        accountName: string | null;
        isActive: boolean;
        lastSyncAt: Date | null;
      }>;
      lastSync?: string;
    }>('/schwab/status'),

  // Post-sync rebuild state. An import or Tools replay leaves the expiry sweep / P&L recalc /
  // journal capture to a cron-drained queue; a routine sync leaves only the two stock jobs.
  // `pending` = any job queued (poll while true, invalidate when it clears); `rebuild` = one of
  // sweep/pnl/capture is queued, i.e. Risk/Positions can show historical expired legs as open and
  // P&L as zero — the only state the banner shows for. `enqueuedAt` is revived to a Date by the
  // `At` suffix rule.
  getDerivationStatus: () =>
    fetchApi<{
      pending: boolean;
      rebuild: boolean;
      jobs: Array<'sweep' | 'pnl' | 'capture' | 'stock' | 'reconcile'>;
      enqueuedAt: Date | null;
    }>('/schwab/derivation-status'),
};

// Import
export const importApi = {
  // Schwab JSON import
  previewJson: (json: unknown) =>
    fetchApi<{
      transactions: Array<{
        date: string;
        action: string;
        symbol: string;
        underlyingSymbol: string;
        optionType: string;
        strikePrice: number;
        expirationDate: string;
        quantity: number;
        price: number;
        fees: number;
        amount: number;
        valid: boolean;
        error?: string;
        groupId?: string;
      }>;
      summary: {
        total: number;
        valid: number;
        invalid: number;
        opening: number;
        closing: number;
        expired: number;
        estimatedPositions: number;
      };
    }>('/import/json/preview', {
      method: 'POST',
      body: JSON.stringify({ json }),
    }),

  executeJson: (json: unknown) =>
    fetchApi<{
      success: boolean;
      transactionsProcessed: number;
      chainsCreated: number;
      positionsCreated: number;
      positionsClosed: number;
      errors?: string[];
    }>('/import/json/execute', {
      method: 'POST',
      body: JSON.stringify({ json }),
    }),

  // Streaming version with progress updates
  executeJsonStream: async (
    json: unknown,
    onProgress: (progress: {
      phase: 'parsing' | 'processing' | 'finalizing' | 'complete';
      current: number;
      total: number;
      chainsCreated: number;
      positionsCreated: number;
      positionsClosed: number;
      message?: string;
    }) => void
  ): Promise<{
    success: boolean;
    transactionsProcessed: number;
    chainsCreated: number;
    positionsCreated: number;
    positionsClosed: number;
    errors?: string[];
    note?: string;
  }> => {
    // Raw fetch (SSE stream, not a JSON entity body) — bypasses fetchApi, so
    // apiDateReviver never runs here. Any date-shaped fields in progress
    // events or the final result stay plain strings by design.
    const response = await fetch('/api/import/json/execute-stream-v3', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ json }),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Unknown error' }));
      throw new ApiError(response.status, error.error || error.message || 'Request failed');
    }

    const reader = response.body?.getReader();
    if (!reader) throw new Error('No response body');

    const decoder = new TextDecoder();
    let buffer = '';
    let result: {
      success: boolean;
      transactionsProcessed: number;
      chainsCreated: number;
      positionsCreated: number;
      positionsClosed: number;
      errors?: string[];
      note?: string;
    } | null = null;

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split('\n\n');
      buffer = lines.pop() || '';

      for (const line of lines) {
        if (line.startsWith('data: ')) {
          try {
            const data = JSON.parse(line.slice(6));
            if (data.type === 'result') {
              result = data.data;
            } else if (data.type === 'error') {
              throw new Error(data.error);
            } else if (data.phase) {
              onProgress(data);
            }
          } catch (e) {
            if (e instanceof Error && e.message !== 'Unexpected end of JSON input') {
              throw e;
            }
          }
        }
      }
    }

    if (!result) throw new Error('No result received from import');
    return result;
  },

  clearDataPreview: () =>
    fetchApi<{
      counts: {
        chains: number;
        positions: number;
        legs: number;
        transactions: number;
      };
    }>('/import/clear/preview'),

  clearData: () =>
    fetchApi<{
      success: boolean;
      deleted: {
        chains: number;
        positions: number;
        legs: number;
        transactions: number;
      };
    }>('/import/clear', {
      method: 'DELETE',
    }),

  recalculatePnl: () =>
    fetchApi<{
      success: boolean;
      updated: number;
      failed: number;
      total: number;
    }>('/import/recalculate-pnl', {
      method: 'POST',
    }),

  fixHistoricalDates: () =>
    fetchApi<{
      success: boolean;
      positionsFixed: number;
      chainsFixed: number;
    }>('/import/fix-historical-dates', {
      method: 'POST',
    }),

  closeExpired: () =>
    fetchApi<{
      success: boolean;
      positionsClosed: number;
    }>('/import/close-expired', {
      method: 'POST',
    }),

  fixMissingCloseTransactions: () =>
    fetchApi<{
      success: boolean;
      transactionsCreated: number;
      chainsUpdated: number;
    }>('/import/fix-missing-close-transactions', {
      method: 'POST',
    }),
};

// Stock positions
export interface StockTransaction {
  id: string;
  transactionType: 'buy' | 'sell';
  quantity: number;
  pricePerShare: number;
  fees: number;
  source: 'assignment' | 'exercise' | 'manual_sale' | 'import';
  executedAt: Date;
}

export interface StockPosition {
  id: string;
  underlyingSymbol: string;
  quantity: number;
  quantityRemaining: number;
  costBasisPerShare: number;
  origin: 'assignment' | 'exercise' | 'direct_purchase';
  status: 'open' | 'closed';
  realizedPnl: number;
  tradeChainId: string | null;
  openedAt: Date;
  closedAt: Date | null;
  transactions?: StockTransaction[];
}

export const stockApi = {
  getOpen: () =>
    fetchApi<StockPosition[]>('/stock-positions?status=open'),
  getBySymbol: (symbol: string) =>
    fetchApi<StockPosition[]>(`/stock-positions?symbol=${symbol}&status=open`),
  getAllBySymbol: (symbol: string) =>
    fetchApi<StockPosition[]>(`/stock-positions?symbol=${symbol}`),
};

// Orders
export interface OpenOrder {
  orderId: number;
  status: string;
  orderType: string;
  complexOrderStrategyType: string | null;
  price: number | null;
  quantity: number;
  filledQuantity: number;
  enteredTime: string;
  legs: Array<{
    instruction: string;
    quantity: number;
    symbol: string;
    assetType: string;
    underlyingSymbol: string | null;
    putCall: 'PUT' | 'CALL' | null;
    strikePrice: number | null;
    expirationDate: string | null;
    positionEffect: string;
  }>;
}

export interface OrderAnalysis {
  limitPrice: number | null;
  naturalPrice: number;
  midPrice: number;
  spreadGap: number;
  limitVsNatural: number;
  fillDifficulty: 'easy' | 'moderate' | 'hard' | 'unlikely';
  legs: Array<{
    instruction: string;
    strikePrice: number | null;
    putCall: string | null;
    expirationDate: string | null;
    bid: number | null;
    ask: number | null;
    mark: number | null;
    spread: number | null;
  }>;
  suggestions: string[];
}

export const ordersApi = {
  getOpen: () =>
    fetchApi<{ orders: OpenOrder[] }>('/orders/open'),

  getBySymbol: (symbol: string) =>
    fetchApi<{ orders: OpenOrder[] }>(`/orders/by-symbol/${encodeURIComponent(symbol)}`),

  analyze: (params: {
    underlyingSymbol: string;
    orderType: string;
    limitPrice: number | null;
    legs: Array<{
      instruction: string;
      putCall: 'PUT' | 'CALL' | null;
      strikePrice: number | null;
      expirationDate: string | null;
      quantity: number;
      assetType: string;
    }>;
  }) =>
    fetchApi<{ analysis: OrderAnalysis }>('/orders/analyze', {
      method: 'POST',
      body: JSON.stringify(params),
    }),
};

// Tools
export const toolsApi = {
  recalculatePnl: () =>
    fetchApi<{
      success: boolean;
      updated: number;
      failed: number;
      total: number;
    }>('/import/recalculate-pnl', {
      method: 'POST',
    }),

  fixChainStatuses: () =>
    fetchApi<{
      success: boolean;
      expiredClosed: number;
      positionsFixed: number;
      stockPositionsClosed: number;
      chainsRecalculated: number;
    }>('/import/fix-chain-statuses', {
      method: 'POST',
    }),

  rebuildStockPositions: () =>
    fetchApi<{
      success: boolean;
      stockPositionsCreated: number;
      stockPositionsOpen: number;
      stockPositionsClosed: number;
      stockTransactionsCreated: number;
      chainsRecalculated: number;
    }>('/import/rebuild-stock-positions', {
      method: 'POST',
    }),

  // Task 17: force-matches Steward's open stock positions against Schwab's live account
  // holdings — the final derivation step, run after rebuildStockPositions. Lifted from a raw
  // fetch in ToolsPage.tsx into the api object (Task 16's house pattern) so it can also be
  // called from the replay/import follow-up chains.
  reconcileStockPositions: () =>
    fetchApi<{
      success: boolean;
      schwabEquityCount: number;
      changes: string[];
    }>('/import/reconcile-stock-positions', {
      method: 'POST',
    }),

  reconcile: async (file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    // Raw fetch (multipart FormData upload, not a fetchApi JSON call) —
    // apiDateReviver never runs here; response fields stay plain strings.
    const response = await fetch('/api/import/reconcile', {
      method: 'POST',
      body: formData,
      credentials: 'include',
    });
    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Unknown error' }));
      throw new Error((error as { error: string }).error || 'Reconciliation failed');
    }
    return response.json() as Promise<{
      summary: {
        schwabTotal: number;
        stewardTotal: number;
        difference: number;
        symbolsMatched: number;
        symbolsMismatched: number;
        symbolsOnlyInSchwab: number;
        symbolsOnlyInSteward: number;
      };
      symbols: Array<{
        symbol: string;
        schwabPnl: number;
        stewardPnl: number;
        difference: number;
        status: 'match' | 'mismatch' | 'schwab_only' | 'steward_only';
      }>;
    }>;
  },

  resetTradeData: () =>
    fetchApi<{
      success: boolean;
      deleted: {
        transactions: string;
        stockPositions: number;
        legTransactions: number;
        positionLegs: number;
        positions: number;
        tradeChains: number;
        alertHistory: string;
      };
    }>('/import/reset-trade-data', {
      method: 'POST',
    }),
};

// Backfill
/** The probed API floor: earlier start dates return nothing. Mirrors
 * DEFAULT_BACKFILL_START in packages/api/src/routes/backfill.ts — kept as a
 * separate literal because the client, not the server, now owns chunking
 * (see the Task 8 brief's correction below). */
const BACKFILL_DEFAULT_START = new Date('2024-03-25T00:00:00.000Z');

/** ~90 days. Matches the server's own per-chunk cap (CHUNK_MS in
 * backfill.ts), so each client-driven request maps to exactly one chunk on
 * the server side rather than triggering its own internal sub-loop. */
const BACKFILL_CHUNK_MS = 90 * 24 * 60 * 60 * 1000;

/**
 * Pure, and exported for testing: no clock, no I/O.
 *
 * The server's `/api/backfill/schwab-transactions` endpoint accepts a single
 * `{ start, end }` range and would happily loop every chunk itself — but a
 * single request looping ~10 quarters server-side costs roughly
 * `10 + T/50 + T/4` subrequests, which crosses the Workers 1000-subrequest
 * cap well within plausible transaction counts for this window. The client
 * drives one quarter per request instead: each call costs ~40-50
 * subrequests, trivially safe, and the run becomes resumable and visibly
 * incremental for free.
 *
 * Produces contiguous ~90-day ranges covering `[start, end]` with no gaps and
 * no overrun — `out[i].start === out[i - 1].end` for every adjacent pair, and
 * the very first start / very last end match the requested bounds exactly. A
 * gap here would silently skip a window of trades and would present as "the
 * API just does not have those", indistinguishable from a real coverage
 * limit — this property matters more than the chunk size does. No range
 * exceeds a year: Schwab's `/transactions` rejects a longer span with HTTP
 * 400.
 *
 * Returns ISO strings, not Dates — this is exactly the shape the POST body
 * needs, and every date-ish field this backfill flow carries stays a string
 * end to end (raw fetch + SSE bypasses fetchApi, so apiDateReviver never
 * runs; see "API / Web Client Dates" in CLAUDE.md).
 *
 * `end <= start` returns an empty array — there is no span to cover.
 */
export function backfillQuarterRanges(
  start: Date = BACKFILL_DEFAULT_START,
  end: Date = new Date()
): Array<{ start: string; end: string }> {
  if (end.getTime() <= start.getTime()) return [];

  const ranges: Array<{ start: string; end: string }> = [];
  let chunkStart = start;

  while (chunkStart.getTime() < end.getTime()) {
    const proposedEnd = new Date(chunkStart.getTime() + BACKFILL_CHUNK_MS);
    const chunkEnd = proposedEnd.getTime() < end.getTime() ? proposedEnd : end;
    ranges.push({ start: chunkStart.toISOString(), end: chunkEnd.toISOString() });
    chunkStart = chunkEnd;
  }

  return ranges;
}

/** The server's literal SSE payload shape — deliberately NOT reshaped toward
 * `ImportProgress` (`current`/`total`); the two are different event types.
 * `chunk`/`totalChunks` describe the server's OWN internal sub-chunking of
 * the single range it was given, which is always 1-of-1 in the per-quarter
 * calling convention below — the "quarter N of M" the UI shows comes from
 * the client's own loop index, not these fields. */
export interface BackfillChunkEvent {
  phase: 'chunk' | 'error' | 'complete';
  chunk: number;
  totalChunks: number;
  inserted: number;
  skipped: number;
  message: string;
}

export const backfillApi = {
  /**
   * Posts ONE quarter range and streams its SSE progress to completion.
   * Never loops ranges itself — see `backfillQuarterRanges` above and the
   * Task 8 brief's correction. Resolves with the server's terminal event —
   * `phase: 'complete'` on success, `phase: 'error'` if any chunk it covered
   * failed (Task 9b: the server used to send a `complete` trailer
   * unconditionally, with zeroed counts on failure, so the resolved value
   * alone could not tell a failed quarter apart from a genuinely empty one.
   * The terminal event's own `phase` now tells the truth, and its `message`
   * carries the underlying error text — callers do not need to watch
   * `onProgress` for an intervening `error` phase just to detect failure).
   * The trailer is always the last SSE message of the stream, sent strictly
   * after any per-chunk `error` events, so tracking the last
   * complete-or-error event here always lands on it.
   */
  runSchwabTransactionsQuarter: async (
    range: { start: string; end: string },
    onProgress: (event: BackfillChunkEvent) => void
  ): Promise<BackfillChunkEvent> => {
    // Raw fetch (SSE stream, not a JSON entity body) — bypasses fetchApi, so
    // apiDateReviver never runs here. Every field in BackfillChunkEvent stays
    // a plain string/number by design; there is no date field to mistype.
    const response = await fetch('/api/backfill/schwab-transactions', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(range),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Unknown error' }));
      throw new ApiError(response.status, error.error || error.message || 'Request failed');
    }

    const reader = response.body?.getReader();
    if (!reader) throw new Error('No response body');

    const decoder = new TextDecoder();
    let buffer = '';
    let final: BackfillChunkEvent | null = null;

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split('\n\n');
      buffer = lines.pop() || '';

      for (const line of lines) {
        if (!line.startsWith('data: ')) continue;
        const data = JSON.parse(line.slice(6)) as BackfillChunkEvent;
        onProgress(data);
        if (data.phase === 'complete' || data.phase === 'error') {
          final = data;
        }
      }
    }

    if (!final) throw new Error('No result received from backfill');
    return final;
  },
};

/** The server's literal SSE payload shape for `/import/replay`. Every intermediate
 * `processOrders` phase collapses to `'processing'` server-side — see the route's own doc
 * comment; the counts only appear on the terminal event. */
export interface ReplayProgressEvent {
  phase: 'processing' | 'complete' | 'error';
  current: number;
  total: number;
  message: string;
  transactionsRead?: number;
  ordersDerived?: number;
  stockOrdersDerived?: number;
  chainsCreated?: number;
  positionsCreated?: number;
  positionsClosed?: number;
}

export const replayApi = {
  /**
   * Posts to `/import/replay` and streams its SSE progress to completion, mirroring
   * `backfillApi.runSchwabTransactionsQuarter` exactly: raw fetch (not `fetchApi`, so
   * `apiDateReviver` never runs — every field here stays a plain string/number by design),
   * `getReader()`, buffer split on `\n\n`, and the resolved value is the terminal
   * (`'complete'` or `'error'`) event.
   */
  run: async (onProgress: (event: ReplayProgressEvent) => void): Promise<ReplayProgressEvent> => {
    const response = await fetch('/api/import/replay', {
      method: 'POST',
      credentials: 'include',
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Unknown error' }));
      throw new ApiError(response.status, error.error || error.message || 'Request failed');
    }

    const reader = response.body?.getReader();
    if (!reader) throw new Error('No response body');

    const decoder = new TextDecoder();
    let buffer = '';
    let final: ReplayProgressEvent | null = null;

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split('\n\n');
      buffer = lines.pop() || '';

      for (const line of lines) {
        if (!line.startsWith('data: ')) continue;
        const data = JSON.parse(line.slice(6)) as ReplayProgressEvent;
        onProgress(data);
        if (data.phase === 'complete' || data.phase === 'error') {
          final = data;
        }
      }
    }

    if (!final) throw new Error('No result received from replay');
    return final;
  },
};

export interface Transaction {
  id: string;
  userId: string;
  source: string;
  schwabOrderId: string | null;
  fingerprint: string;
  transactionType: string;
  underlyingSymbol: string | null;
  optionSymbol: string | null;
  optionType: string | null;
  strikePrice: number | null;
  expirationDate: Date | null;
  action: string;
  positionEffect: string | null;
  quantity: number;
  price: number;
  amount: number;
  fees: number;
  splitRatio: number | null;
  dividendType: string | null;
  executedAt: Date;
  rawData: Record<string, unknown> | null;
  acctgRuleCd: string | null;
  createdAt: Date;
  costBasis: number | null;
  tradeChainId: string | null;
}

export interface TransactionSummary {
  totalTransactions: number;
  totalFees: number;
  byType: Array<{
    type: string;
    count: number;
    totalAmount: number;
    totalFees: number;
  }>;
  dividends: {
    bySymbol: Array<{ symbol: string; total: number; count: number }>;
    total: number;
  };
}

export const transactionsApi = {
  list: (params?: {
    page?: number;
    limit?: number;
    type?: string;
    symbol?: string;
    from?: string;
    to?: string;
    sort?: 'asc' | 'desc';
  }) => {
    const searchParams = new URLSearchParams();
    if (params?.page) searchParams.set('page', String(params.page));
    if (params?.limit) searchParams.set('limit', String(params.limit));
    if (params?.type) searchParams.set('type', params.type);
    if (params?.symbol) searchParams.set('symbol', params.symbol);
    if (params?.from) searchParams.set('from', params.from);
    if (params?.to) searchParams.set('to', params.to);
    if (params?.sort) searchParams.set('sort', params.sort);
    const qs = searchParams.toString();
    return fetchApi<{
      transactions: Transaction[];
      pagination: { page: number; limit: number; total: number; totalPages: number };
    }>(`/transactions${qs ? `?${qs}` : ''}`);
  },

  summary: (params?: { symbol?: string; type?: string; from?: string; to?: string }) => {
    const searchParams = new URLSearchParams();
    if (params?.symbol) searchParams.set('symbol', params.symbol);
    if (params?.type) searchParams.set('type', params.type);
    if (params?.from) searchParams.set('from', params.from);
    if (params?.to) searchParams.set('to', params.to);
    const qs = searchParams.toString();
    return fetchApi<TransactionSummary>(`/transactions/summary${qs ? `?${qs}` : ''}`);
  },

  income: () =>
    fetchApi<{
      dividends: Transaction[];
      interest: Transaction[];
    }>('/transactions/income'),

  corporateActions: () =>
    fetchApi<{
      actions: Transaction[];
    }>('/transactions/corporate-actions'),

  reconcile: (jsonData: unknown) =>
    fetchApi<{
      summary: {
        uploaded: number;
        matched: number;
        missing: number;
        matchPercent: number;
      };
      results: Array<{
        date: string;
        action: string;
        symbol: string;
        quantity: number;
        amount: number;
        fees: number;
        status: 'matched' | 'missing' | 'amount_mismatch';
        transactionType: string;
      }>;
    }>('/import/reconcile-transactions', {
      method: 'POST',
      body: JSON.stringify({ json: jsonData }),
    }),
};

// AI
export interface AiChatMessage {
  role: 'user' | 'assistant';
  content: string;
}

export const aiApi = {
  analyzePosition: (positionId: string) =>
    fetchApi<PositionAnalysis>(`/ai/positions/${positionId}/analyze`, { method: 'POST' }),

  // Raw fetch (SSE) — bypasses fetchApi/apiDateReviver by design; unlike
  // executeJsonStream this endpoint is tenant-scoped, so attach X-Tenant-Id.
  chatPosition: async (
    positionId: string,
    messages: AiChatMessage[],
    onDelta: (text: string) => void,
  ): Promise<void> => {
    const tenantId = getActiveTenantId();
    const response = await fetch(`/api/ai/positions/${positionId}/chat`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(tenantId ? { 'X-Tenant-Id': tenantId } : {}),
      },
      credentials: 'include',
      body: JSON.stringify({ messages }),
    });
    if (!response.ok) {
      let message = 'Chat request failed';
      try {
        message = (await response.json() as { error?: string }).error ?? message;
      } catch { /* keep default */ }
      throw new ApiError(response.status, message);
    }

    const reader = response.body?.getReader();
    if (!reader) throw new Error('No response body');
    const decoder = new TextDecoder();
    let buffer = '';
    let done = false;

    while (true) {
      const { done: streamDone, value } = await reader.read();
      if (streamDone) break;
      buffer += decoder.decode(value, { stream: true });
      const chunks = buffer.split('\n\n');
      buffer = chunks.pop() || '';
      for (const chunk of chunks) {
        if (!chunk.startsWith('data: ')) continue;
        const data = JSON.parse(chunk.slice(6)) as
          | { type: 'text'; text: string }
          | { type: 'done' }
          | { type: 'error'; error: string };
        if (data.type === 'text') onDelta(data.text);
        else if (data.type === 'error') throw new Error(data.error);
        else if (data.type === 'done') done = true;
      }
    }
    if (!done) throw new Error('Chat stream ended unexpectedly');
  },
};
